import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { AppSelect, type AppSelectOption } from "../components/AppSelect";
import { useClientDatasets, useClients } from "../hooks/useClients";
import type { DatasetDashboardType, DatasetQueryName, DatasetSettings, DatasetSummary } from "../types";
import { useAppQueryClient } from "../lib/queryClient";
import { markFrontMilestone, usePagePerfTrace } from "../lib/perfAudit";
import { apiFetch } from "../lib/api";

const PROCESSING_REFRESH_POLL_INTERVAL_MS = 5_000;
const PROCESSING_REFRESH_POLL_ATTEMPTS = 24;

export function ClientDatasetsPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const perfTrace = usePagePerfTrace("ClientDatasetsPage");
  const queryClient = useAppQueryClient();
  const { data: clients } = useClients({
    perfTrace,
    perfLabel: "query.clients.client_datasets_page",
  });
  const { data, isLoading, error } = useClientDatasets(clientId, {
    perfTrace,
    perfLabel: "query.client_datasets.client_datasets_page",
  });

  const client = clients?.find((item) => item.id === clientId);
  const datasets = data ?? [];
  const clientDisplayName = client?.name ?? clientId ?? "Client";

  const [isEditingClientName, setIsEditingClientName] = useState(false);
  const [clientNameDraft, setClientNameDraft] = useState("");
  const [isSavingClientName, setIsSavingClientName] = useState(false);
  const [clientNameError, setClientNameError] = useState<string | null>(null);

  useEffect(() => {
    setClientNameDraft(clientDisplayName);
    setIsEditingClientName(false);
    setClientNameError(null);
  }, [clientDisplayName]);

  useEffect(() => {
    if (isLoading || error || !clientId || !data) return;

    markFrontMilestone(perfTrace, "first_meaningful_data", {
      clientId,
      datasetCount: data.length,
      hasClientMetadata: Boolean(client),
    });
  }, [client, clientId, data, error, isLoading, perfTrace]);

  const stats = useMemo(() => {
    const totalShipments = datasets.reduce((sum, dataset) => sum + dataset.shipmentCount, 0);
    const countries = new Set(datasets.flatMap((dataset) => dataset.countries)).size;
    return { totalShipments, countries };
  }, [datasets]);

  const handleCancelClientRename = () => {
    setClientNameDraft(clientDisplayName);
    setClientNameError(null);
    setIsEditingClientName(false);
  };

  const handleSaveClientRename = async () => {
    if (!clientId || isSavingClientName) return;
    const nextName = clientNameDraft.trim();
    if (!nextName) {
      setClientNameError("Le nom du client ne peut pas être vide.");
      return;
    }

    setIsSavingClientName(true);
    setClientNameError(null);
    try {
      const response = await apiFetch(`/api/clients/${encodeURIComponent(clientId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: nextName }),
      });
      if (!response.ok) {
        throw new Error(`Impossible de renommer le client (${response.status})`);
      }
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "datasets"] });
      setIsEditingClientName(false);
    } catch (renameError) {
      setClientNameError((renameError as Error).message);
    } finally {
      setIsSavingClientName(false);
    }
  };

  return (
    <div className="cdv-page-wrapper">
      <nav className="cdv-top-nav" aria-label="Navigation principale client">
        <div className="cdv-top-nav-inner">
          <div className="cdv-logo-section">
            <Link to="/" className="cdv-logo-link">
              <span>Lainvision</span>
              <span className="cdv-particle-logo" aria-hidden="true"></span>
            </Link>
            <div className="cdv-breadcrumb" aria-label="Fil d'Ariane">
              <Link to="/clients" className="cdv-breadcrumb-link">
                Clients
              </Link>
              <span className="cdv-breadcrumb-separator" aria-hidden="true">
                /
              </span>
              <span className="cdv-breadcrumb-current">{clientDisplayName}</span>
            </div>
          </div>
        </div>
      </nav>

      <section className="cdv-hero-section">
        <div className="cdv-hero-inner">
          <Link to="/clients" className="cdv-back-link-hero" aria-label="Retour aux clients">
            <span aria-hidden="true">←</span>
            <span>Retour aux clients</span>
          </Link>

          <div className="cdv-hero-content">
            <div className="cdv-hero-main">
              <div className="cdv-hero-kicker">
                <span>Client</span>
              </div>

              <div className="cdv-hero-title-section">
                {isEditingClientName ? (
                  <input
                    className="cdv-setting-input cdv-client-name-input"
                    value={clientNameDraft}
                    onChange={(event) => setClientNameDraft(event.target.value)}
                    placeholder="Nom du client"
                    aria-label="Renommer le client"
                  />
                ) : (
                  <h1 className="cdv-hero-title">{clientDisplayName}</h1>
                )}

                {!isEditingClientName ? (
                  <button
                    type="button"
                    className="cdv-btn cdv-btn-secondary cdv-btn-sm"
                    onClick={() => setIsEditingClientName(true)}
                  >
                    Renommer
                  </button>
                ) : null}
              </div>

              {isEditingClientName ? (
                <div className="cdv-settings-actions">
                  <button
                    type="button"
                    className="cdv-btn cdv-btn-primary cdv-btn-sm"
                    onClick={handleSaveClientRename}
                    disabled={isSavingClientName}
                  >
                    {isSavingClientName ? "Enregistrement..." : "Enregistrer"}
                  </button>
                  <button
                    type="button"
                    className="cdv-btn cdv-btn-secondary cdv-btn-sm"
                    onClick={handleCancelClientRename}
                    disabled={isSavingClientName}
                  >
                    Annuler
                  </button>
                </div>
              ) : null}

              {clientNameError ? <p className="cdv-error">{clientNameError}</p> : null}

              <div className="cdv-hero-kpis">
                <div className="cdv-mini-kpi">
                  <div className="cdv-mini-kpi-label">Datasets</div>
                  <div className="cdv-mini-kpi-value">{datasets.length}</div>
                </div>
                <div className="cdv-mini-kpi">
                  <div className="cdv-mini-kpi-label">Expéditions</div>
                  <div className="cdv-mini-kpi-value">{stats.totalShipments}</div>
                </div>
                <div className="cdv-mini-kpi">
                  <div className="cdv-mini-kpi-label">Pays</div>
                  <div className="cdv-mini-kpi-value">{stats.countries}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="cdv-main-content">
        <div className="cdv-section-header">
          <h2 className="cdv-section-title">Datasets disponibles</h2>
        </div>

        {isLoading ? <p className="cdv-hint">Chargement des datasets...</p> : null}
        {error ? (
          <p role="alert" className="cdv-error">
            Erreur : {error.message}
          </p>
        ) : null}

        {datasets.length > 0 ? (
          <section className="cdv-datasets-grid" aria-label="Liste des datasets">
            {datasets.map((dataset) => (
              <DatasetCard key={dataset.id} dataset={dataset} clientId={clientId ?? ""} />
            ))}
          </section>
        ) : null}

        {!isLoading && datasets.length === 0 ? (
          <p className="cdv-hint">Aucun dataset disponible pour ce client.</p>
        ) : null}
      </main>
    </div>
  );
}

type DatasetCardProps = {
  dataset: DatasetSummary;
  clientId: string;
};

const DASHBOARD_LABELS: Record<DatasetDashboardType, string> = {
  operations: "Opérations",
  temperature: "Température",
};

const TRI_STATE_OPTIONS: AppSelectOption[] = [
  { value: "auto", label: "Auto" },
  { value: "yes", label: "Oui" },
  { value: "no", label: "Non" },
];

const REVERSE_MAP_OPTIONS: AppSelectOption[] = [
  { value: "auto", label: "Auto" },
  { value: "yes", label: "Activer" },
  { value: "no", label: "Désactiver" },
];

function DatasetCard({ dataset, clientId }: DatasetCardProps) {
  const queryClient = useAppQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [draftName, setDraftName] = useState(dataset.name);
  const [dashboardTypeDraft, setDashboardTypeDraft] = useState<DatasetDashboardType>(dataset.dashboardType);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [settingsDraft, setSettingsDraft] = useState<DatasetSettingsDraft>(toSettingsDraft(defaultDatasetSettings()));
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isRefreshingProcessing, setIsRefreshingProcessing] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSuccess, setSettingsSuccess] = useState<string | null>(null);
  const [showAdvancedQueries, setShowAdvancedQueries] = useState(false);
  const initialSettings = useMemo(() => defaultDatasetSettings(), []);

  const settingsQuery = useQuery<DatasetSettingsResponse>(
    {
      queryKey: ["clients", clientId, "datasets", dataset.id, "settings"],
      queryFn: async () => {
        const response = await apiFetch(
          `/api/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(dataset.id)}/settings`,
        );
        if (!response.ok) {
          throw new Error(`Impossible de charger les paramètres (${response.status})`);
        }
        return (await response.json()) as DatasetSettingsResponse;
      },
      enabled: Boolean(clientId) && isSettingsOpen,
      staleTime: 60_000,
      placeholderData: (previous) => previous,
    },
    queryClient,
  );

  const effectiveSettings = settingsQuery.data?.settings ?? initialSettings;
  const availableProfiles = settingsQuery.data?.availableQueryProfiles ?? [];
  const profileListId = `dataset-profile-${dataset.id.replace(/[^A-Za-z0-9_-]/g, "_")}`;
  const configuredSettingsCount = countConfiguredSettings(effectiveSettings);
  const isSettingsCountPending = isSettingsOpen && !settingsQuery.data && (settingsQuery.isLoading || settingsQuery.isFetching);
  const dashboardOptions = useMemo<AppSelectOption[]>(
    () =>
      dataset.dashboardOptions.map((option) => ({
        value: option,
        label: DASHBOARD_LABELS[option] ?? option,
      })),
    [dataset.dashboardOptions],
  );

  const invalidateDatasetQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "datasets"] });
    await queryClient.invalidateQueries({ queryKey: ["dataset", clientId, dataset.id] });
    await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "datasets", dataset.id, "settings"] });
    await queryClient.invalidateQueries({ queryKey: ["consignment", clientId, dataset.id] });
  };

  const pollDatasetRefresh = async () => {
    for (let attempt = 0; attempt < PROCESSING_REFRESH_POLL_ATTEMPTS; attempt += 1) {
      await wait(PROCESSING_REFRESH_POLL_INTERVAL_MS);
      await invalidateDatasetQueries();
    }
  };

  useEffect(() => {
    setDraftName(dataset.name);
  }, [dataset.name]);

  useEffect(() => {
    setDashboardTypeDraft(dataset.dashboardType);
  }, [dataset.dashboardType]);

  useEffect(() => {
    setSettingsDraft(toSettingsDraft(effectiveSettings));
  }, [effectiveSettings]);

  const handleCancel = () => {
    setDraftName(dataset.name);
    setSaveError(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!clientId || isSaving) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await apiFetch(
        `/api/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(dataset.id)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: draftName }),
        },
      );
      if (!response.ok) {
        throw new Error(`Impossible de renommer (${response.status})`);
      }
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "datasets"] });
      await queryClient.invalidateQueries({ queryKey: ["dataset", clientId, dataset.id] });
      setIsEditing(false);
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const saveSettingsAndDashboard = async () => {
    if (!clientId) throw new Error("Client introuvable.");

    const response = await apiFetch(
      `/api/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(dataset.id)}/settings`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fromSettingsDraft(settingsDraft)),
      },
    );
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
      throw new Error(payload?.error?.message ?? `Impossible de sauvegarder (${response.status})`);
    }

    if (dashboardTypeDraft === dataset.dashboardType) return;

    const dashboardResponse = await apiFetch(
      `/api/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(dataset.id)}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dashboardType: dashboardTypeDraft }),
      },
    );
    if (!dashboardResponse.ok) {
      const payload = (await dashboardResponse.json().catch(() => null)) as { error?: { message?: string } } | null;
      throw new Error(payload?.error?.message ?? `Impossible de sauvegarder le dashboard (${dashboardResponse.status})`);
    }
  };

  const handleSaveSettings = async () => {
    if (!clientId || isSavingSettings) return;
    setIsSavingSettings(true);
    setSettingsError(null);
    setSettingsSuccess(null);
    try {
      await saveSettingsAndDashboard();

      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "datasets"] });
      await queryClient.invalidateQueries({ queryKey: ["dataset", clientId, dataset.id] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "datasets", dataset.id, "settings"] });
      setSettingsSuccess("Paramètres enregistrés.");
    } catch (err) {
      setSettingsError((err as Error).message);
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleRefreshProcessing = async () => {
    if (!clientId || isRefreshingProcessing) return;
    setIsRefreshingProcessing(true);
    setSettingsError(null);
    setSettingsSuccess(null);
    try {
      await saveSettingsAndDashboard();

      const response = await apiFetch(
        `/api/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(dataset.id)}/materialize`,
        { method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() } },
      );
      const payload = (await response.json().catch(() => null)) as {
        job?: { state?: string };
        error?: { message?: string };
      } | null;

      if (!response.ok) {
        throw new Error(payload?.error?.message ?? `Impossible de relancer le traitement (${response.status})`);
      }

      if (payload?.job?.state === "queued" || payload?.job?.state === "running") {
        await invalidateDatasetQueries();
        setSettingsSuccess("Paramètres enregistrés. Matérialisation lancée en arrière-plan.");
        void pollDatasetRefresh();
        return;
      }

      await invalidateDatasetQueries();
      setSettingsSuccess("Paramètres enregistrés et rafraîchissement demandé.");
    } catch (err) {
      setSettingsError((err as Error).message);
    } finally {
      setIsRefreshingProcessing(false);
    }
  };

  return (
    <article className="cdv-dataset-card">
      <div className="cdv-dataset-card-header">
        <div className="cdv-dataset-card-title-section">
          {!isEditing ? (
            <Link
              to={`/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(dataset.id)}`}
              className="cdv-dataset-card-link"
              aria-label={`Ouvrir le dataset ${dataset.name}`}
            >
              <h3 className="cdv-dataset-card-title">{dataset.name}</h3>
            </Link>
          ) : (
            <input
              className="cdv-setting-input"
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              placeholder="Nom du dataset"
              aria-label={`Renommer ${dataset.name}`}
            />
          )}
          <p className="cdv-dataset-card-subtitle">ID: {dataset.id}</p>
        </div>

        <div className="cdv-dataset-card-header-actions">
          {!isEditing && dataset.canAdmin ? (
            <button
              type="button"
              className="cdv-btn cdv-btn-secondary cdv-btn-sm"
              onClick={() => setIsEditing(true)}
            >
              Renommer
            </button>
          ) : null}
        </div>
      </div>

      {isEditing ? (
        <div className="cdv-settings-actions">
          <button
            type="button"
            className="cdv-btn cdv-btn-primary cdv-btn-sm"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Enregistrement..." : "Enregistrer"}
          </button>
          <button
            type="button"
            className="cdv-btn cdv-btn-secondary cdv-btn-sm"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Annuler
          </button>
        </div>
      ) : null}

      {saveError ? <p className="cdv-error">{saveError}</p> : null}

      <div className="cdv-dataset-card-stats">
        <div className="cdv-stat-box">
          <div className="cdv-stat-box-value">{dataset.shipmentCount}</div>
          <div className="cdv-stat-box-label">Expéditions</div>
        </div>
      </div>

      <div className="cdv-dataset-card-details">
        <div className="cdv-detail-row">
          <span className="cdv-detail-label">Destinations</span>
          <span className="cdv-detail-value">{renderCountries(dataset.countries)}</span>
        </div>
      </div>

      <div className="cdv-dataset-card-footer">
        <span className="cdv-badge cdv-badge-orange">
          {dataset.dashboardType === "temperature" ? "Température" : "Opérations"}
        </span>
        {!isEditing ? (
          <Link
            to={`/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(dataset.id)}`}
            className="cdv-view-button"
            aria-label={`Voir les détails du dataset ${dataset.name}`}
          >
            <span>Voir les détails</span>
            <span aria-hidden="true">→</span>
          </Link>
        ) : null}
      </div>

      {dataset.canAdmin ? (
        <details
          className="cdv-dataset-settings-disclosure"
          onToggle={(event) => {
            const open = (event.currentTarget as HTMLDetailsElement).open;
            setIsSettingsOpen(open);
            if (!open) {
              setShowAdvancedQueries(false);
            }
          }}
        >
        <summary className="cdv-disclosure-summary">
          <span>Paramètres du dataset</span>
          <span
            className="cdv-disclosure-count"
            title={
              !isSettingsOpen
                ? "Ouvrir pour charger les paramètres"
                : isSettingsCountPending
                ? "Chargement des paramètres..."
                : renderSettingsSummary(effectiveSettings)
            }
          >
            {!isSettingsOpen
              ? "Ouvrir"
              : isSettingsCountPending
              ? "Calcul..."
              : `${configuredSettingsCount} configuration${configuredSettingsCount > 1 ? "s" : ""}`}
          </span>
        </summary>

        <div className="cdv-disclosure-content">
          {settingsQuery.isLoading ? <p className="cdv-hint">Chargement des paramètres...</p> : null}
          {settingsQuery.isError ? (
            <p className="cdv-error">Erreur paramètres: {(settingsQuery.error as Error).message}</p>
          ) : null}

          {!settingsQuery.isLoading && !settingsQuery.isError ? (
            <>
              <div className="cdv-settings-section">
                <h4 className="cdv-settings-section-title">Informations générales</h4>
                <div className="cdv-settings-grid">
                  <div className="cdv-setting-field">
                    <label htmlFor={`set-profile-${dataset.id}`}>Profil requêtes SPARQL</label>
                    <input
                      id={`set-profile-${dataset.id}`}
                      className="cdv-setting-input"
                      list={profileListId}
                      value={settingsDraft.queryProfile}
                      onChange={(event) => {
                        setSettingsDraft((current) => ({ ...current, queryProfile: event.target.value }));
                        setSettingsSuccess(null);
                      }}
                      placeholder="Auto (_defaults/core, msd/msdah, ...)"
                    />
                    <datalist id={profileListId}>
                      {availableProfiles.map((profile) => (
                        <option key={profile} value={profile} />
                      ))}
                    </datalist>
                  </div>
                </div>
              </div>

              <div className="cdv-settings-section">
                <h4 className="cdv-settings-section-title">Paramètres de visualisation</h4>
                <div className="cdv-settings-grid">
                  <div className="cdv-setting-field">
                    <label htmlFor={`set-dashboard-${dataset.id}`}>Dashboard</label>
                    <AppSelect
                      id={`set-dashboard-${dataset.id}`}
                      value={dashboardTypeDraft}
                      options={dashboardOptions}
                      onValueChange={(value) => {
                        setDashboardTypeDraft(value as DatasetDashboardType);
                        setSettingsSuccess(null);
                      }}
                      disabled={dashboardOptions.length <= 1}
                    />
                  </div>

                  <div className="cdv-setting-field">
                    <label htmlFor={`set-city-${dataset.id}`}>Données ville</label>
                    <AppSelect
                      id={`set-city-${dataset.id}`}
                      value={settingsDraft.hasCity}
                      options={TRI_STATE_OPTIONS}
                      onValueChange={(value) => {
                        setSettingsDraft((current) => ({
                          ...current,
                          hasCity: toTriStateValue(value),
                        }));
                        setSettingsSuccess(null);
                      }}
                    />
                  </div>

                  <div className="cdv-setting-field">
                    <label htmlFor={`set-boxes-${dataset.id}`}>Afficher boîtes</label>
                    <AppSelect
                      id={`set-boxes-${dataset.id}`}
                      value={settingsDraft.hasBoxes}
                      options={TRI_STATE_OPTIONS}
                      onValueChange={(value) => {
                        setSettingsDraft((current) => ({
                          ...current,
                          hasBoxes: toTriStateValue(value),
                        }));
                        setSettingsSuccess(null);
                      }}
                    />
                  </div>

                  <div className="cdv-setting-field">
                    <label htmlFor={`set-reverse-${dataset.id}`}>Vue reverse map</label>
                    <AppSelect
                      id={`set-reverse-${dataset.id}`}
                      value={settingsDraft.hasReverseGeocoding}
                      options={REVERSE_MAP_OPTIONS}
                      onValueChange={(value) => {
                        setSettingsDraft((current) => ({
                          ...current,
                          hasReverseGeocoding: toTriStateValue(value),
                        }));
                        setSettingsSuccess(null);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="cdv-settings-section">
                <h4 className="cdv-settings-section-title">Paramètres de température</h4>
                <div className="cdv-settings-temperature">
                  <div className="cdv-settings-row">
                    <div className="cdv-setting-field">
                      <label htmlFor={`set-min-${dataset.id}`}>Seuil excursion min (°C)</label>
                      <input
                        id={`set-min-${dataset.id}`}
                        className="cdv-setting-input"
                        type="number"
                        step="0.1"
                        value={settingsDraft.temperatureMin}
                        onChange={(event) => {
                          setSettingsDraft((current) => ({ ...current, temperatureMin: event.target.value }));
                          setSettingsSuccess(null);
                        }}
                        placeholder="Auto"
                      />
                    </div>

                    <div className="cdv-setting-field">
                      <label htmlFor={`set-max-${dataset.id}`}>Seuil excursion max (°C)</label>
                      <input
                        id={`set-max-${dataset.id}`}
                        className="cdv-setting-input"
                        type="number"
                        step="0.1"
                        value={settingsDraft.temperatureMax}
                        onChange={(event) => {
                          setSettingsDraft((current) => ({ ...current, temperatureMax: event.target.value }));
                          setSettingsSuccess(null);
                        }}
                        placeholder="Auto"
                      />
                    </div>
                  </div>

                  <div className="cdv-settings-row">
                    <div className="cdv-setting-field">
                      <label htmlFor={`set-chart-min-${dataset.id}`}>Ordonnée min courbe (°C)</label>
                      <input
                        id={`set-chart-min-${dataset.id}`}
                        className="cdv-setting-input"
                        type="number"
                        step="0.1"
                        value={settingsDraft.chartTemperatureMin}
                        onChange={(event) => {
                          setSettingsDraft((current) => ({ ...current, chartTemperatureMin: event.target.value }));
                          setSettingsSuccess(null);
                        }}
                        placeholder="Auto"
                      />
                    </div>

                    <div className="cdv-setting-field">
                      <label htmlFor={`set-chart-max-${dataset.id}`}>Ordonnée max courbe (°C)</label>
                      <input
                        id={`set-chart-max-${dataset.id}`}
                        className="cdv-setting-input"
                        type="number"
                        step="0.1"
                        value={settingsDraft.chartTemperatureMax}
                        onChange={(event) => {
                          setSettingsDraft((current) => ({ ...current, chartTemperatureMax: event.target.value }));
                          setSettingsSuccess(null);
                        }}
                        placeholder="Auto"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {showAdvancedQueries ? (
                <div className="cdv-settings-section">
                  <h4 className="cdv-settings-section-title">Requêtes SPARQL avancées</h4>
                  <div className="cdv-settings-query-grid">
                    {DATASET_QUERY_KEYS.map((queryKey) => (
                      <div className="cdv-setting-field" key={queryKey}>
                        <label htmlFor={`set-query-${dataset.id}-${queryKey}`}>Requête {queryKey}</label>
                        <textarea
                          id={`set-query-${dataset.id}-${queryKey}`}
                          className="cdv-setting-textarea"
                          rows={5}
                          value={settingsDraft.queryOverrides[queryKey]}
                          onChange={(event) => {
                            setSettingsDraft((current) => ({
                              ...current,
                              queryOverrides: {
                                ...current.queryOverrides,
                                [queryKey]: event.target.value,
                              },
                            }));
                            setSettingsSuccess(null);
                          }}
                          placeholder="Laisser vide pour utiliser le profil/default."
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="cdv-settings-actions">
                <button
                  type="button"
                  className="cdv-btn cdv-btn-primary cdv-btn-sm"
                  onClick={handleSaveSettings}
                  disabled={isSavingSettings || isRefreshingProcessing}
                >
                  {isSavingSettings ? "Enregistrement..." : "Enregistrer les modifications"}
                </button>

                <button
                  type="button"
                  className="cdv-btn cdv-btn-secondary cdv-btn-sm"
                  onClick={() => {
                    setDashboardTypeDraft(dataset.dashboardType);
                    setSettingsDraft(toSettingsDraft(effectiveSettings));
                    setSettingsError(null);
                    setSettingsSuccess(null);
                  }}
                  disabled={isSavingSettings || isRefreshingProcessing}
                >
                  Réinitialiser
                </button>

                <button
                  type="button"
                  className="cdv-btn cdv-btn-secondary cdv-btn-sm"
                  onClick={handleRefreshProcessing}
                  disabled={isSavingSettings || isRefreshingProcessing}
                >
                  {isRefreshingProcessing ? "Rafraîchissement..." : "Forcer le rafraîchissement du traitement"}
                </button>

                <button
                  type="button"
                  className="cdv-btn cdv-btn-secondary cdv-btn-sm"
                  onClick={() => setShowAdvancedQueries((prev) => !prev)}
                  disabled={isSavingSettings || isRefreshingProcessing}
                >
                  {showAdvancedQueries ? "Masquer requêtes avancées" : "Déplier requêtes avancées"}
                </button>
              </div>

              {settingsError ? <p className="cdv-error">{settingsError}</p> : null}
              {settingsSuccess ? <p className="cdv-success">{settingsSuccess}</p> : null}
            </>
          ) : null}
        </div>
        </details>
      ) : null}
    </article>
  );
}

type DatasetSettingsResponse = {
  settings: DatasetSettings;
  availableQueryProfiles: string[];
};

type DatasetSettingsDraft = {
  hasCity: "auto" | "yes" | "no";
  hasBoxes: "auto" | "yes" | "no";
  hasReverseGeocoding: "auto" | "yes" | "no";
  temperatureMin: string;
  temperatureMax: string;
  chartTemperatureMin: string;
  chartTemperatureMax: string;
  queryProfile: string;
  queryOverrides: Record<DatasetQueryName, string>;
};

const DATASET_QUERY_KEYS: DatasetQueryName[] = ["id", "info", "sensorValue", "events", "movements"];

function defaultDatasetSettings(): DatasetSettings {
  return {
    hasCity: null,
    hasBoxes: null,
    hasReverseGeocoding: null,
    temperatureMin: null,
    temperatureMax: null,
    chartTemperatureMin: null,
    chartTemperatureMax: null,
    queryProfile: null,
    queryOverrides: {},
  };
}

function toSettingsDraft(settings: DatasetSettings): DatasetSettingsDraft {
  return {
    hasCity: toTriState(settings.hasCity),
    hasBoxes: toTriState(settings.hasBoxes),
    hasReverseGeocoding: toTriState(settings.hasReverseGeocoding),
    temperatureMin: settings.temperatureMin === null ? "" : String(settings.temperatureMin),
    temperatureMax: settings.temperatureMax === null ? "" : String(settings.temperatureMax),
    chartTemperatureMin: settings.chartTemperatureMin === null ? "" : String(settings.chartTemperatureMin),
    chartTemperatureMax: settings.chartTemperatureMax === null ? "" : String(settings.chartTemperatureMax),
    queryProfile: settings.queryProfile ?? "",
    queryOverrides: {
      id: settings.queryOverrides.id ?? "",
      info: settings.queryOverrides.info ?? "",
      sensorValue: settings.queryOverrides.sensorValue ?? "",
      events: settings.queryOverrides.events ?? "",
      movements: settings.queryOverrides.movements ?? "",
    },
  };
}

function fromSettingsDraft(draft: DatasetSettingsDraft): DatasetSettings {
  return {
    hasCity: fromTriState(draft.hasCity),
    hasBoxes: fromTriState(draft.hasBoxes),
    hasReverseGeocoding: fromTriState(draft.hasReverseGeocoding),
    temperatureMin: parseOptionalNumber(draft.temperatureMin),
    temperatureMax: parseOptionalNumber(draft.temperatureMax),
    chartTemperatureMin: parseOptionalNumber(draft.chartTemperatureMin),
    chartTemperatureMax: parseOptionalNumber(draft.chartTemperatureMax),
    queryProfile: normalizeOptionalText(draft.queryProfile),
    queryOverrides: normalizeQueryOverrides(draft.queryOverrides),
  };
}

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeOptionalText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function toTriState(value: boolean | null): "auto" | "yes" | "no" {
  if (value === true) return "yes";
  if (value === false) return "no";
  return "auto";
}

function toTriStateValue(value: string): "auto" | "yes" | "no" {
  if (value === "yes" || value === "no") return value;
  return "auto";
}

function fromTriState(value: "auto" | "yes" | "no"): boolean | null {
  if (value === "yes") return true;
  if (value === "no") return false;
  return null;
}

function normalizeQueryOverrides(
  queryOverrides: Record<DatasetQueryName, string>,
): Partial<Record<DatasetQueryName, string>> {
  const out: Partial<Record<DatasetQueryName, string>> = {};
  for (const key of DATASET_QUERY_KEYS) {
    const trimmed = queryOverrides[key].trim();
    if (trimmed) out[key] = trimmed;
  }
  return out;
}

function renderSettingsSummary(settings: DatasetSettings): string {
  const min = settings.temperatureMin === null ? "auto" : settings.temperatureMin.toString();
  const max = settings.temperatureMax === null ? "auto" : settings.temperatureMax.toString();
  const axisMin = settings.chartTemperatureMin === null ? "auto" : settings.chartTemperatureMin.toString();
  const axisMax = settings.chartTemperatureMax === null ? "auto" : settings.chartTemperatureMax.toString();
  const city = settings.hasCity === null ? "city auto" : settings.hasCity ? "city oui" : "city non";
  const boxes = settings.hasBoxes === null ? "boîtes auto" : settings.hasBoxes ? "boîtes oui" : "boîtes non";
  const reverse =
    settings.hasReverseGeocoding === null
      ? "reverse auto"
      : settings.hasReverseGeocoding
      ? "reverse on"
      : "reverse off";
  return `seuil ${min}/${max}°C • axe ${axisMin}/${axisMax}°C • ${city} • ${boxes} • ${reverse}`;
}

function countConfiguredSettings(settings: DatasetSettings): number {
  let count = 0;
  if (settings.hasCity !== null) count += 1;
  if (settings.hasBoxes !== null) count += 1;
  if (settings.hasReverseGeocoding !== null) count += 1;
  if (settings.temperatureMin !== null) count += 1;
  if (settings.temperatureMax !== null) count += 1;
  if (settings.chartTemperatureMin !== null) count += 1;
  if (settings.chartTemperatureMax !== null) count += 1;
  if (settings.queryProfile?.trim()) count += 1;
  for (const key of DATASET_QUERY_KEYS) {
    if (settings.queryOverrides[key]?.trim()) {
      count += 1;
    }
  }
  return count;
}

function wait(durationMs: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, durationMs);
  });
}

function renderCountries(countries: string[]): string {
  if (!countries.length) return "Aucune";
  if (countries.length <= 3) return countries.join(", ");
  return `${countries.slice(0, 3).join(", ")} +${countries.length - 3}`;
}
